/**
 * Release Board filters, persisted in the URL query string so links keep them.
 *
 * URL params: q, creator, labels (csv names), version (all|original|variations|lang:<code>), needed, media.
 * The URL is the single source of truth; every column and the counts read the
 * same parsed object.
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { parseLanguageCode } from '@/utils/languages';
import {
  BOARD_MEDIA_TYPE_VALUES,
  BOARD_SEARCH_MAX_LENGTH,
  BOARD_NEEDED_BY_VALUES,
  BOARD_LANG_VERSION_PREFIX,
  BOARD_VERSION_VALUES,
  EMPTY_BOARD_FILTERS,
  type BoardFilters,
  type BoardMediaTypeFilter,
  type BoardNeededByFilter,
  type BoardVersionFilter,
} from '@/types/releaseBoard';

const MAX_LABELS = 20;
const MAX_LABEL_LENGTH = 100;

const PARAM = {
  q: 'q',
  creator: 'creator',
  labels: 'labels',
  version: 'version',
  needed: 'needed',
  media: 'media',
} as const;

function parsePositiveInt(value: string | null): number | null {
  if (!value || !/^\d+$/.test(value)) return null;
  const n = Number(value);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

function pickOne<T extends string>(value: string | null, allowed: readonly T[]): T | null {
  return allowed.find((candidate) => candidate === value) ?? null;
}

/** `all|original|variations` or `lang:<code>` (code as the backend stores it, lowercase); anything else = all. */
function parseVersion(value: string | null): BoardVersionFilter {
  const known = pickOne<BoardVersionFilter>(value, BOARD_VERSION_VALUES);
  if (known) return known;
  const code = (value ?? '').startsWith(BOARD_LANG_VERSION_PREFIX)
    ? parseLanguageCode((value ?? '').slice(BOARD_LANG_VERSION_PREFIX.length))
    : null;
  return code ? `${BOARD_LANG_VERSION_PREFIX}${code}` : 'all';
}

export function parseBoardFilters(params: URLSearchParams): BoardFilters {
  const labels: string[] = [];
  for (const part of (params.get(PARAM.labels) ?? '').split(',')) {
    const name = part.trim().slice(0, MAX_LABEL_LENGTH);
    if (name && !labels.some((l) => l.toLowerCase() === name.toLowerCase()) && labels.length < MAX_LABELS) {
      labels.push(name);
    }
  }
  return {
    q: (params.get(PARAM.q) ?? '').slice(0, BOARD_SEARCH_MAX_LENGTH),
    creatorId: parsePositiveInt(params.get(PARAM.creator)),
    labels,
    version: parseVersion(params.get(PARAM.version)),
    neededBy: pickOne<BoardNeededByFilter>(params.get(PARAM.needed), BOARD_NEEDED_BY_VALUES),
    mediaType: pickOne<BoardMediaTypeFilter>(params.get(PARAM.media), BOARD_MEDIA_TYPE_VALUES),
  };
}

function writeBoardFilters(params: URLSearchParams, filters: BoardFilters): void {
  const set = (key: string, value: string | null): void => {
    if (value) params.set(key, value);
    else params.delete(key);
  };
  set(PARAM.q, filters.q.trim() || null);
  set(PARAM.creator, filters.creatorId !== null ? String(filters.creatorId) : null);
  set(PARAM.labels, filters.labels.length > 0 ? filters.labels.join(',') : null);
  set(PARAM.version, filters.version !== 'all' ? filters.version : null);
  set(PARAM.needed, filters.neededBy);
  set(PARAM.media, filters.mediaType);
}

/** Number of active filter groups (labels count as one group). */
export function countActiveBoardFilters(filters: BoardFilters): number {
  return (
    (filters.q.trim() ? 1 : 0) +
    (filters.creatorId !== null ? 1 : 0) +
    (filters.labels.length > 0 ? 1 : 0) +
    (filters.version !== 'all' ? 1 : 0) +
    (filters.neededBy ? 1 : 0) +
    (filters.mediaType ? 1 : 0)
  );
}

export interface UseReleaseBoardFilters {
  filters: BoardFilters;
  activeCount: number;
  setFilters: (patch: Partial<BoardFilters>) => void;
  clearAll: () => void;
}

export function useReleaseBoardFilters(): UseReleaseBoardFilters {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseBoardFilters(searchParams), [searchParams]);

  const setFilters = useCallback(
    (patch: Partial<BoardFilters>): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          writeBoardFilters(next, { ...parseBoardFilters(prev), ...patch });
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const clearAll = useCallback((): void => setFilters(EMPTY_BOARD_FILTERS), [setFilters]);

  return { filters, activeCount: countActiveBoardFilters(filters), setFilters, clearAll };
}
